import * as AsyncData from "foldkit/asyncData";
import type { Html, HtmlBuilder } from "foldkit/html";
import { defineView } from "foldkit/submodel";

import type { ApiError } from "../../api/errors";
import type {
	RateCardHistory,
	RateCardVersion,
} from "../../api/schemas/ledger";
import { i18next } from "../../i18n";
import type { Shared } from "../shared";
import type { Message } from "./message";
import type { Model } from "./model";

type Rates = RateCardVersion["rates"];

/** v1 `RateVersionTable`'s reduce: insertion-ordered groups by category. */
export const groupByCategory = (
	rates: Rates,
): ReadonlyArray<readonly [string, Rates]> => {
	const groups = new Map<string, Array<Rates[number]>>();
	for (const rate of rates) {
		groups.set(rate.category, [...(groups.get(rate.category) ?? []), rate]);
	}
	return [...groups.entries()];
};

const versionTable = <Msg>(
	h: HtmlBuilder<Msg>,
	version: RateCardVersion,
): Html =>
	h.div(
		[h.Class("card bg-base-100 shadow")],
		[
			h.div(
				[h.Class("card-body")],
				[
					h.div(
						[h.Class("flex flex-wrap items-baseline gap-x-4 gap-y-1")],
						[
							h.h3(
								[h.Class("card-title text-base-content/80")],
								[`${i18next.t("credits.rateCardVersion")} ${version.version}`],
							),
							h.span(
								[h.Class("text-sm text-base-content/50")],
								[
									`${i18next.t("credits.rateCardEffective")}: ${version.effectiveDate}`,
								],
							),
						],
					),
					version.notes
						? h.p(
								[h.Class("text-sm text-base-content/60 mt-1")],
								[version.notes],
							)
						: h.empty,
					h.div(
						[h.Class("overflow-x-auto mt-3")],
						[
							h.table(
								[h.Class("table table-sm")],
								[
									h.thead(
										[],
										[
											h.tr(
												[h.Class("text-base-content/60")],
												[
													h.th([], [i18next.t("credits.rateCardCategory")]),
													h.th([], [i18next.t("credits.rateCardItem")]),
													h.th([], [i18next.t("credits.rateCardUnit")]),
													h.th(
														[h.Class("text-right")],
														[i18next.t("credits.rateCardOurCost")],
													),
													h.th(
														[h.Class("text-right")],
														[i18next.t("credits.rateCardYourCost")],
													),
												],
											),
										],
									),
									h.tbody(
										[],
										groupByCategory(version.rates).flatMap(
											([category, rates]) =>
												rates.map((rate, index) =>
													h.tr(
														[h.Key(`${category}-${rate.item}`)],
														[
															index === 0
																? h.td(
																		[
																			h.Rowspan(rates.length),
																			h.Class("font-medium align-top"),
																		],
																		[category],
																	)
																: h.empty,
															h.td([], [rate.item]),
															h.td(
																[h.Class("text-base-content/60")],
																[rate.unit],
															),
															h.td(
																[h.Class("text-right text-base-content/60")],
																[rate.ourCost],
															),
															h.td(
																[h.Class("text-right font-semibold")],
																[rate.yourCost],
															),
														],
													),
												),
										),
									),
								],
							),
						],
					),
				],
			),
		],
	);

/** v1 `RateCardPanel`: shared by the Rate Card page and CreditUsage's Rates tab. */
export const rateCardPanel = <Msg>(
	h: HtmlBuilder<Msg>,
	history: AsyncData.AsyncData<RateCardHistory, ApiError>,
): Html => {
	if (AsyncData.isLoading(history)) {
		return h.div(
			[h.Class("py-12 text-center text-base-content/60"), h.Role("status")],
			[
				h.span([h.Class("loading loading-spinner loading-md")], []),
				h.p([h.Class("mt-3")], [i18next.t("credits.rateCardLoading")]),
			],
		);
	}
	const versions = AsyncData.getOrElse(history, () => ({
		versions: [],
	})).versions;
	if (AsyncData.isFailure(history) || versions.length === 0) {
		return h.div(
			[h.Class("alert alert-error"), h.Role("alert")],
			[h.span([], [i18next.t("credits.rateCardError")])],
		);
	}
	return h.div(
		[h.Class("space-y-6")],
		[
			h.section(
				[],
				[
					h.h2(
						[h.Class("text-xl font-bold text-base-content")],
						[i18next.t("credits.rateCardHistory")],
					),
					h.p(
						[h.Class("mt-1 text-base-content/60 max-w-2xl")],
						[i18next.t("credits.rateCardHistoryDescription")],
					),
				],
			),
			...[...versions].reverse().map((version) => versionTable(h, version)),
		],
	);
};

export const view = defineView<Model, Message, Shared>((model, _shared, h) =>
	h.div(
		[
			h.DataAttribute("testid", "page-RateCard"),
			h.Class("container mx-auto px-4 py-8"),
		],
		[rateCardPanel(h, model.history)],
	),
);
